// SunSystem — pure state + math, no rendering side effects.
//
// Tracks the apartment's geographic location (lat / lon), the simulated date,
// and the time of day as a 0..24 fractional hour. From those, computes solar
// **azimuth** (0 = north, 90 = east, 180 = south, 270 = west) and
// **elevation** (0 = horizon, 90 = zenith, negative = below horizon → night).
//
// Subscribers:
//   • App.handleSunChange — feeds the panorama emissive tint with a sunlight
//     curve (warm at dawn/dusk, cool at midnight, neutral at noon).
//   • Dollhouse.setSunAzimuth — orange wedge on the perimeter that points
//     away from the sun (idea #9 from DOLLHOUSE-IDEAS.md).
//   • Future glTF rooms — directional pc.LightComponent rotation.
//
// Accuracy: the formula is a standard NOAA-style approximation good to ~1°
// for daylight study purposes. We deliberately ignore equation of time and
// timezone for the slider so 12:00 reads as "solar noon" — the architect's
// mental model. For real-world calibration the operator can adjust lat/lon
// or shift the hour by their longitude's solar offset.

import { Emitter } from '../app/emitter';

export interface SunState {
  /** Reference date — sets the time of year (solar declination). */
  date: Date;
  /** Apartment latitude, decimal degrees (positive = N, negative = S). */
  latitude: number;
  /** Apartment longitude, decimal degrees (positive = E, negative = W). */
  longitude: number;
  /** Local solar time of day, 0..24 fractional hours. */
  hoursOfDay: number;
  /** Solar azimuth in degrees (0 = north, 90 = east, 180 = south, 270 = west). */
  azimuthDeg: number;
  /** Solar elevation in degrees (0 = horizon, 90 = zenith, negative = below horizon). */
  elevationDeg: number;
}

export interface SunSystemOptions {
  latitude: number;
  longitude: number;
  /** Defaults to "today" (now in local time). */
  date?: Date;
  /** Defaults to the current hour-of-day from the provided date. */
  hoursOfDay?: number;
}

export class SunSystem {
  private state: SunState;
  private readonly emitter = new Emitter<SunState>();

  constructor(opts: SunSystemOptions) {
    const date = opts.date ?? new Date();
    const hoursOfDay = opts.hoursOfDay ?? (date.getHours() + date.getMinutes() / 60);
    const solar = computeSolar(date, opts.latitude, opts.longitude, hoursOfDay);
    this.state = {
      date,
      latitude: opts.latitude,
      longitude: opts.longitude,
      hoursOfDay,
      azimuthDeg: solar.azimuthDeg,
      elevationDeg: solar.elevationDeg,
    };
  }

  getState(): SunState {
    return this.state;
  }

  on(listener: (state: SunState) => void): () => void {
    return this.emitter.on(listener);
  }

  setTimeOfDay(hours: number): void {
    const clamped = Math.max(0, Math.min(24, hours));
    if (Math.abs(clamped - this.state.hoursOfDay) < 0.01) return;
    this.state = { ...this.state, hoursOfDay: clamped };
    this.recompute();
  }

  setLocation(latitude: number, longitude: number): void {
    if (latitude === this.state.latitude && longitude === this.state.longitude) return;
    this.state = { ...this.state, latitude, longitude };
    this.recompute();
  }

  setDate(date: Date): void {
    this.state = { ...this.state, date };
    this.recompute();
  }

  /** Emit the current state without changing it. Useful right after subscribe. */
  emitCurrent(): void {
    this.emitter.emit(this.state);
  }

  private recompute(): void {
    const solar = computeSolar(this.state.date, this.state.latitude, this.state.longitude, this.state.hoursOfDay);
    this.state = { ...this.state, ...solar };
    this.emitter.emit(this.state);
  }
}

/**
 * Solar position from local solar time. NOAA-style approximation, ~1°
 * accuracy — good enough for an architect's daylight slider. We treat the
 * hour parameter as **local solar time** (no equation-of-time correction, no
 * timezone offset) so the slider's "12:00" lines up with solar noon, which
 * matches how architects sketch sun paths.
 */
function computeSolar(date: Date, latDeg: number, lonDeg: number, hours: number):
  { azimuthDeg: number; elevationDeg: number } {
  // Day of year (1..366)
  const start = Date.UTC(date.getUTCFullYear(), 0, 0);
  const day = Math.floor((date.getTime() - start) / 86400000);

  // Solar declination (radians) — Cooper's approximation.
  const declRad = (23.45 * Math.PI / 180) * Math.sin((2 * Math.PI / 365) * (day - 81));

  // Hour angle (radians). 15° per hour, zero at solar noon.
  const hourAngleRad = ((hours - 12) * 15) * Math.PI / 180;

  const latRad = latDeg * Math.PI / 180;

  // Elevation (altitude above horizon)
  const sinAlt = Math.sin(latRad) * Math.sin(declRad) +
                 Math.cos(latRad) * Math.cos(declRad) * Math.cos(hourAngleRad);
  const altRad = Math.asin(clamp(sinAlt, -1, 1));

  // Azimuth — measured from north, clockwise.
  const cosAlt = Math.cos(altRad);
  let azRad: number;
  if (Math.abs(cosAlt) < 1e-6) {
    azRad = 0; // zenith / nadir — azimuth is undefined; pick north
  } else {
    const cosAz = (Math.sin(declRad) - Math.sin(altRad) * Math.sin(latRad)) /
                  (cosAlt * Math.cos(latRad));
    azRad = Math.acos(clamp(cosAz, -1, 1));
    // Hour angle positive = afternoon → sun is on the west side of the sky.
    if (hourAngleRad > 0) azRad = 2 * Math.PI - azRad;
  }

  // Longitude is unused in this simplified solar-time form — we keep the
  // parameter so the SunSystem can correct for it later (clock-time mode).
  void lonDeg;

  return {
    elevationDeg: (altRad * 180) / Math.PI,
    azimuthDeg: (azRad * 180) / Math.PI,
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
